/* SKU ordering uses the current storefront session. No prices or customer records are persisted. */
(function (root, factory) {
  const api = factory(root);
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root && root.document) root.SaberQuickOrder = api;
})(typeof window === "undefined" ? globalThis : window, function (root) {
  "use strict";

  const MAX_ROWS = 100;
  const normalizeSku = (value) =>
    String(value || "")
      .trim()
      .toLocaleUpperCase("en-US");
  const positiveInteger = (value) =>
    /^\d+$/.test(String(value).trim()) &&
    Number.isSafeInteger(Number(value)) &&
    Number(value) > 0;
  const idString = (value) =>
    /^\d+$/.test(String(value || "")) ? String(value) : "";

  function delimiterFor(text) {
    let quoted = false,
      fieldStart = true;
    for (let i = 0; i < text.length; i += 1) {
      if (text[i] === '"' && (quoted || fieldStart)) {
        if (quoted && text[i + 1] === '"') i += 1;
        else quoted = !quoted;
      }
      if (!quoted && text[i] === "\t") return "\t";
      if (!quoted && /[\r\n]/.test(text[i])) break;
      if (!quoted && text[i] === ",") fieldStart = true;
      else if (!/\s/.test(text[i])) fieldStart = false;
    }
    return ",";
  }

  function parseRows(input) {
    const text = String(input || "").replace(/^\uFEFF/, "");
    const delimiter = delimiterFor(text);
    const records = [];
    let fields = [],
      field = "",
      quoted = false,
      closed = false,
      error = "",
      line = 1,
      startLine = 1;
    function finishField() {
      fields.push(field.trim());
      field = "";
      closed = false;
    }
    function finishRow() {
      finishField();
      if (fields.some((value) => value !== "") || error)
        records.push({ fields, line: startLine, error });
      fields = [];
      error = "";
      startLine = line + 1;
    }
    for (let i = 0; i < text.length; i += 1) {
      const ch = text[i];
      if (quoted) {
        if (ch === '"' && text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else if (ch === '"') {
          quoted = false;
          closed = true;
        } else {
          field += ch;
          if (ch === "\n") line += 1;
        }
      } else if (ch === '"' && field.trim() === "" && !closed) {
        quoted = true;
        field = "";
      } else if (ch === delimiter) {
        finishField();
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i += 1;
        finishRow();
        line += 1;
      } else if (closed && !/\s/.test(ch)) {
        error = "Unexpected text after a quoted field.";
        field += ch;
      } else field += ch;
    }
    if (quoted) error = "A quoted field is missing its closing double quote.";
    if (field || fields.length || error) finishRow();
    if (
      records.length &&
      /^(sku|product\s*sku|item\s*sku)$/i.test(records[0].fields[0]) &&
      (records[0].fields.length === 1 ||
        /^(quantity|qty)$/i.test(records[0].fields[1]))
    )
      records.shift();
    const result = records.slice(0, MAX_ROWS).map((record) => {
      const sku = record.fields[0] || "";
      const rawQuantity = record.fields.length === 1 ? "1" : record.fields[1];
      let rowError = record.error;
      if (record.fields.length > 2)
        rowError =
          "Use two columns: SKU and quantity. Put a SKU containing commas in double quotes.";
      else if (!sku) rowError = "Enter a SKU.";
      else if (!positiveInteger(rawQuantity))
        rowError = "Quantity must be a whole number greater than zero.";
      return {
        sku,
        quantity: positiveInteger(rawQuantity)
          ? Number(rawQuantity)
          : rawQuantity,
        line: record.line,
        error: rowError || "",
      };
    });
    if (records.length > MAX_ROWS)
      result.push({
        sku: "",
        quantity: 0,
        line: records[MAX_ROWS].line,
        error:
          "This list has more than 100 products. Split it into smaller lists.",
      });
    return result;
  }

  function candidatesFrom(payload) {
    if (!payload || !Array.isArray(payload.products))
      throw new Error(
        "The catalog response was unavailable. Please try reviewing again.",
      );
    return payload.products.flatMap((product) =>
      [
        ...(Array.isArray(product.variants) ? product.variants : []),
        ...(product.selectedVariant ? [product.selectedVariant] : []),
      ]
        .filter((variant) => idString(variant.id))
        .map((variant) => ({
          ...variant,
          id: String(variant.id),
          sku: String(variant.sku || ""),
          productTitle: String(product.title || "Product"),
          variantTitle:
            variant.title === "Default Title"
              ? ""
              : String(variant.title || ""),
          url: String(product.url || ""),
          image: product.image || "",
          requiresSellingPlan: product.requiresSellingPlan === true,
          min: positiveInteger(variant.min) ? Number(variant.min) : 1,
          increment: positiveInteger(variant.increment)
            ? Number(variant.increment)
            : 1,
          max: positiveInteger(variant.max) ? Number(variant.max) : null,
        })),
    );
  }

  function matchProducts(payload, item, byId) {
    const all = candidatesFrom(payload);
    const seen = new Set();
    const matches = all.filter((variant) => {
      const exact = byId
        ? variant.id === idString(item.variantId)
        : normalizeSku(variant.sku) !== "" &&
          normalizeSku(variant.sku) === normalizeSku(item.sku);
      if (!exact || seen.has(variant.id)) return false;
      seen.add(variant.id);
      return true;
    });
    const incomplete =
      !byId &&
      (payload.truncated === true ||
        payload.products.some((product) => product.variantsTruncated === true));
    let error = "";
    if (incomplete)
      error =
        "The catalog returned a limited result set. Open the product to confirm its SKU, or narrow your entry before ordering.";
    else if (!matches.length)
      error = byId
        ? "This saved product option is no longer available in your catalog."
        : "No exact SKU match in your catalog. Check the full SKU or search for the product.";
    return {
      ...item,
      matches,
      selectedId: !error && matches.length === 1 ? matches[0].id : "",
      incomplete,
      error,
    };
  }

  function productLookupUrl(item, origin) {
    if (!idString(item.variantId) || !item.url) return null;
    const url = new URL(item.url, origin);
    if (url.origin !== origin || !/\/products\/[^/]+\/?$/.test(url.pathname))
      throw new Error("Use a product link from this store for saved products.");
    url.search = "";
    url.hash = "";
    url.searchParams.set("view", "quick-order");
    url.searchParams.set("variant", idString(item.variantId));
    return url;
  }

  async function resolveRows(items, options = {}) {
    if (!Array.isArray(items) || items.length > MAX_ROWS)
      throw new Error("Review up to 100 products at a time.");
    const origin =
      options.origin || root.location?.origin || "https://store.invalid";
    const fetcher = options.fetch || root.fetch.bind(root);
    const signal = options.signal;
    const jobs = new Map();
    const rows = items
      .slice(0, MAX_ROWS + 1)
      .map((item, index) => ({ ...item, line: item.line || index + 1 }));
    rows.forEach((item) => {
      if (item.error) return;
      if (!positiveInteger(item.quantity)) {
        item.error = "Quantity must be a whole number greater than zero.";
        return;
      }
      try {
        const productUrl = productLookupUrl(item, origin);
        if (!productUrl && !normalizeSku(item.sku)) {
          item.error = "A SKU or a valid saved product reference is required.";
          return;
        }
        const url =
          productUrl || new URL(options.searchUrl || "/search", origin);
        if (!productUrl) {
          url.searchParams.set("view", "quick-order");
          url.searchParams.set("type", "product");
          url.searchParams.set("options[prefix]", "none");
          url.searchParams.set(
            "q",
            '"' +
              String(item.sku)
                .trim()
                .replace(/\\/g, "\\\\")
                .replace(/"/g, '\\"') +
              '"',
          );
        }
        item.lookupKey = url.href;
        item.byId = !!productUrl;
        if (!jobs.has(url.href)) jobs.set(url.href, { url: url.href });
      } catch (error) {
        item.error = error.message;
      }
    });
    const queue = Array.from(jobs.values());
    let cursor = 0;
    async function worker() {
      while (cursor < queue.length) {
        if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
        const job = queue[cursor++];
        try {
          const response = await fetcher(job.url, {
            signal,
            credentials: "same-origin",
            cache: "no-store",
            headers: { Accept: "application/json" },
          });
          if (!response.ok)
            throw new Error(
              "Catalog lookup failed. Review again or open the product page.",
            );
          job.payload = await response.json();
          candidatesFrom(job.payload);
        } catch (error) {
          if (signal?.aborted || error.name === "AbortError") throw error;
          job.error = error.message;
        }
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(3, queue.length) }, worker),
    );
    return rows.map((row) => {
      if (row.error) return { ...row, matches: [], selectedId: "" };
      const job = jobs.get(row.lookupKey);
      if (job.error)
        return { ...row, matches: [], selectedId: "", error: job.error };
      return matchProducts(job.payload, row, row.byId);
    });
  }

  function selectedVariant(row) {
    return row.matches?.find(
      (variant) => variant.id === String(row.selectedId),
    );
  }

  function aggregateRows(rows) {
    const grouped = new Map(),
      errors = [];
    rows.forEach((row) => {
      const variant = selectedVariant(row);
      if (row.error || row.incomplete)
        errors.push(`Row ${row.line}: ${row.error || "Review this product."}`);
      else if (!variant)
        errors.push(`Row ${row.line}: choose the correct product option.`);
      else if (!variant.available)
        errors.push(`Row ${row.line}: this option is currently unavailable.`);
      else if (variant.orderable === false)
        errors.push(
          `Row ${row.line}: ${variant.orderBlockReason || "Open this product page to order."}`,
        );
      else if (variant.requiresSellingPlan)
        errors.push(
          `Row ${row.line}: choose a purchase option on the product page.`,
        );
      else if (!positiveInteger(row.quantity))
        errors.push(
          `Row ${row.line}: enter a whole quantity greater than zero.`,
        );
      else {
        const existing = grouped.get(variant.id);
        const quantity = Number(row.quantity) + (existing?.quantity || 0);
        if (!Number.isSafeInteger(quantity))
          errors.push(`Row ${row.line}: the combined quantity is too large.`);
        else grouped.set(variant.id, { ...variant, quantity });
      }
    });
    return { items: [...grouped.values()], errors };
  }

  function cartQuantities(cart) {
    if (!cart || !Array.isArray(cart.items))
      throw new Error(
        "Your cart could not be checked. Please open your cart before ordering.",
      );
    return cart.items.reduce((totals, item) => {
      const id = String(item.variant_id || item.id);
      totals[id] = (totals[id] || 0) + Number(item.quantity || 0);
      return totals;
    }, {});
  }

  function validateAggregate(items, cart) {
    const totals = cartQuantities(cart);
    return items.flatMap((item) => {
      const finalQuantity = item.quantity + (totals[item.id] || 0);
      const name = item.sku || item.productTitle;
      if (finalQuantity < item.min)
        return [
          `${name}: minimum ${item.min} total in cart; you requested ${item.quantity}.`,
        ];
      if (item.max && finalQuantity > item.max)
        return [
          `${name}: maximum ${item.max} total in cart, including ${totals[item.id] || 0} already there.`,
        ];
      if (finalQuantity % item.increment !== 0)
        return [
          `${name}: total cart quantity must be in multiples of ${item.increment}.`,
        ];
      return [];
    });
  }

  function reconcile(items, before, after) {
    const previous = cartQuantities(before),
      current = cartQuantities(after);
    return items.map((item) => {
      const observed = Math.max(
        0,
        (current[item.id] || 0) - (previous[item.id] || 0),
      );
      return {
        ...item,
        added: Math.min(item.quantity, observed),
        remaining: Math.max(0, item.quantity - observed),
      };
    });
  }

  const csvItems = (items) =>
    items
      .map(
        (item) =>
          '"' +
          String(item.sku || "").replace(/"/g, '""') +
          '",' +
          item.quantity,
      )
      .join("\n");

  class QuickOrder extends (root.HTMLElement || class {}) {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.rows = [];
      this.completed = false;
      this.uncertain = false;
      this.input = this.querySelector("[data-qo-input]");
      this.rowsElement = this.querySelector("[data-qo-rows]");
      this.reviewButton = this.querySelector("[data-qo-review]");
      this.addButton = this.querySelector("[data-qo-add]");
      this.saveButton = this.querySelector("[data-qo-save]");
      this.statusElement = this.querySelector("[data-qo-status]");
      this.reviewButton.addEventListener("click", () =>
        this.reviewItems(this.stagedItems || parseRows(this.input.value)),
      );
      this.input.addEventListener("input", () => {
        if (this.adding || this.uncertain) return;
        this.controller?.abort();
        this.rows = [];
        this.completed = false;
        this.stagedItems = null;
        this.render();
        this.setStatus(
          "Your list changed. Review products again before adding.",
        );
      });
      this.addButton?.addEventListener("click", () => this.addItems());
      this.saveButton.addEventListener("click", () => this.saveItems());
      this.onItems = (event) => {
        if (Array.isArray(event.detail?.items))
          this.reviewItems(event.detail.items);
      };
      root.document.addEventListener("saber:quick-order-items", this.onItems);
      this.onSavedReady = () => this.consumeStaged();
      root.document.addEventListener("savedlists:ready", this.onSavedReady);
      root.addEventListener("hashchange", this.onSavedReady);
      this.consumeStaged();
    }

    disconnectedCallback() {
      this.controller?.abort();
      root.document.removeEventListener(
        "saber:quick-order-items",
        this.onItems,
      );
      root.document.removeEventListener("savedlists:ready", this.onSavedReady);
      root.removeEventListener("hashchange", this.onSavedReady);
      this.initialized = false;
    }

    consumeStaged() {
      if (
        root.location.hash !== "#from-saved-list" ||
        !root.SaberSavedLists?.consumeStagedItems
      )
        return;
      const items = root.SaberSavedLists.consumeStagedItems();
      if (Array.isArray(items) && items.length) this.reviewItems(items);
      else
        this.setStatus(
          "No saved list is available for this account or location. Open your purchasing lists and review the list again.",
          true,
        );
    }

    setStatus(message, error = false) {
      this.statusElement.textContent = message;
      this.statusElement.dataset.error = String(error);
    }

    async reviewItems(items) {
      if (this.adding || this.uncertain) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.rows = [];
      this.completed = false;
      this.render();
      if (!Array.isArray(items) || !items.length) {
        this.setStatus("Enter at least one SKU to review.", true);
        return;
      }
      if (items.length > MAX_ROWS) {
        this.setStatus(
          "Review up to 100 products at a time. Split your list into smaller orders.",
          true,
        );
        return;
      }
      this.stagedItems = items.map((item) => ({ ...item }));
      this.input.value = csvItems(items);
      this.setAttribute("aria-busy", "true");
      this.setStatus("Matching your SKUs against the live catalog…");
      try {
        const rows = await resolveRows(items, {
          searchUrl: this.dataset.searchUrl,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        this.rows = rows;
        this.render();
        const { errors } = aggregateRows(rows);
        this.setStatus(
          errors.length
            ? "Review the marked rows. Every product must be resolved before the list can be added."
            : "Products matched. Check quantities, then add your reviewed items together.",
          !!errors.length,
        );
      } catch (error) {
        if (!controller.signal.aborted)
          this.setStatus(
            error.message ||
              "Catalog lookup was unavailable. Please review again.",
            true,
          );
      } finally {
        if (this.controller === controller) this.removeAttribute("aria-busy");
      }
    }

    reviewedItems() {
      const { items, errors } = aggregateRows(this.rows);
      if (errors.length) return [];
      return items.map((item) => ({
        sku: item.sku,
        quantity: item.quantity,
        variantId: item.id,
        title: item.productTitle,
        url: item.url,
      }));
    }

    saveItems() {
      const items = this.reviewedItems();
      if (!items.length) return;
      if (root.SaberSavedLists?.openSaveDialog) {
        try {
          root.SaberSavedLists.openSaveDialog(items);
        } catch (error) {
          this.setStatus(
            error.message ||
              "This list could not be saved. Please review the quantities.",
            true,
          );
        }
      } else {
        const event = new CustomEvent("saber:save-list", {
          bubbles: true,
          cancelable: true,
          detail: { items, source: "quick-order" },
        });
        if (this.dispatchEvent(event))
          this.setStatus(
            "Purchasing lists could not be opened. Refresh the page and try again.",
            true,
          );
      }
    }

    element(tag, className, text) {
      const node = root.document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    safeUrl(value, image = false) {
      if (typeof value !== "string" || !value.trim()) return "";
      try {
        const url = new URL(value, root.location.origin);
        return /^(https?:)$/.test(url.protocol) &&
          (image || url.origin === root.location.origin)
          ? url.href
          : "";
      } catch (_) {
        return "";
      }
    }

    variantUrl(variant) {
      const safe = this.safeUrl(variant.url);
      if (!safe) return this.dataset.searchUrl;
      const url = new URL(safe);
      url.searchParams.set("variant", variant.id);
      return url.href;
    }

    render() {
      this.rowsElement.replaceChildren();
      this.querySelector("[data-qo-empty]").hidden = this.rows.length > 0;
      this.querySelector("[data-qo-actions]").hidden = this.rows.length === 0;
      this.querySelector("[data-qo-count]").textContent = this.rows.length
        ? `${this.rows.length} rows`
        : "";
      this.rows.forEach((row, index) => {
        const article = this.element("article", "ss-qo-row");
        const main = this.element("div");
        main.append(
          this.element(
            "p",
            "ss-qo-row-meta",
            `Row ${row.line} · SKU: ${row.sku || "Saved product"}`,
          ),
        );
        if (row.matches?.length > 1 && !row.incomplete) {
          const selectId = `qo-match-${this.id || "main"}-${index}`;
          const label = this.element(
            "label",
            "",
            "Choose the exact product option",
          );
          label.htmlFor = selectId;
          const select = this.element("select");
          select.id = selectId;
          const empty = this.element(
            "option",
            "",
            "Select a matching product…",
          );
          empty.value = "";
          select.append(empty);
          row.matches.forEach((variant) => {
            const option = this.element(
              "option",
              "",
              `${variant.productTitle}${variant.variantTitle ? " / " + variant.variantTitle : ""} — ${variant.priceText || "View price"}${variant.available ? "" : " — unavailable"}`,
            );
            option.value = variant.id;
            option.selected = row.selectedId === variant.id;
            select.append(option);
          });
          select.disabled = !!this.adding || this.completed;
          select.addEventListener("change", () => {
            row.selectedId = select.value;
            this.render();
            const { errors } = aggregateRows(this.rows);
            this.setStatus(
              errors.length
                ? "Review the marked rows before adding your list."
                : "Products matched. Check quantities, then add your reviewed items together.",
              !!errors.length,
            );
          });
          main.append(label, select);
        }
        const variant = selectedVariant(row);
        if (variant) {
          const product = this.element("div", "ss-qo-row-product");
          const imageUrl = this.safeUrl(variant.image, true);
          if (imageUrl) {
            const image = this.element("img");
            image.src = imageUrl;
            image.alt = "";
            image.width = 72;
            image.height = 72;
            image.loading = "lazy";
            image.addEventListener(
              "error",
              () => {
                const placeholder = this.element(
                  "span",
                  "ss-qo-row-placeholder",
                  "▧",
                );
                placeholder.setAttribute("aria-hidden", "true");
                image.replaceWith(placeholder);
              },
              { once: true },
            );
            product.append(image);
          } else {
            const placeholder = this.element(
              "span",
              "ss-qo-row-placeholder",
              "▧",
            );
            placeholder.setAttribute("aria-hidden", "true");
            product.append(placeholder);
          }
          const detail = this.element("div");
          const title = this.element(
            "a",
            "ss-qo-row-title",
            variant.productTitle,
          );
          title.href = this.variantUrl(variant);
          detail.append(title);
          if (variant.variantTitle)
            detail.append(
              this.element("p", "ss-qo-row-meta", variant.variantTitle),
            );
          detail.append(
            this.element(
              "p",
              "ss-qo-row-price",
              variant.priceText || "See product price",
            ),
          );
          if (variant.priceBreaks?.length)
            detail.append(
              this.element(
                "p",
                "ss-qo-row-rule",
                "Base unit price shown. Quantity pricing: " +
                  variant.priceBreaks
                    .map(
                      (tier) =>
                        `${tier.quantity}+ units: ${tier.priceText} each`,
                    )
                    .join(" · ") +
                  ". Your cart confirms the applicable price.",
              ),
            );
          detail.append(
            this.element(
              "p",
              variant.available ? "ss-qo-row-availability" : "ss-qo-row-error",
              variant.available
                ? variant.orderable === false
                  ? "Order through the product page"
                  : "Available to order"
                : "Currently unavailable",
            ),
          );
          detail.append(
            this.element(
              "p",
              "ss-qo-row-rule",
              `Minimum ${variant.min} · Multiples of ${variant.increment}${variant.max ? " · Maximum " + variant.max : ""}`,
            ),
          );
          if (variant.requiresSellingPlan)
            detail.append(
              this.element(
                "p",
                "ss-qo-row-error",
                "Choose a purchase option on the product page.",
              ),
            );
          if (variant.orderable === false) {
            detail.append(
              this.element(
                "p",
                "ss-qo-row-error",
                variant.orderBlockReason ||
                  "Review the ordering details on this product page.",
              ),
            );
            const productLink = this.element(
              "a",
              "ss-qo-row-link",
              "View product & ordering options",
            );
            productLink.href = this.variantUrl(variant);
            detail.append(productLink);
          }
          product.append(detail);
          main.append(product);
        }
        if (row.error)
          main.append(this.element("p", "ss-qo-row-error", row.error));
        if (!row.selectedId && row.matches?.length > 1 && !row.incomplete)
          main.append(
            this.element(
              "p",
              "ss-qo-row-error",
              "More than one option uses this SKU. Choose the one you need.",
            ),
          );
        if (row.error || !variant) {
          const link = this.element("a", "ss-qo-row-link", "Search this SKU");
          const url = new URL(this.dataset.searchUrl, root.location.origin);
          url.searchParams.set("q", row.sku || "");
          url.searchParams.set("type", "product");
          link.href = url.href;
          main.append(link);
        }
        const quantityWrap = this.element("div", "ss-qo-row-quantity");
        const quantityId = `qo-qty-${this.id || "main"}-${index}`;
        const quantityLabel = this.element("label", "", "Quantity");
        quantityLabel.htmlFor = quantityId;
        const quantity = this.element("input");
        quantity.id = quantityId;
        quantity.type = "number";
        quantity.min = "1";
        quantity.step = "1";
        quantity.inputMode = "numeric";
        quantity.value = row.quantity;
        quantity.disabled = !!this.adding || this.completed;
        quantity.addEventListener("input", () => {
          row.quantity = quantity.value;
          const quantityError = /Quantity must/.test(row.error || "");
          if (quantityError && positiveInteger(quantity.value)) row.error = "";
          if (this.stagedItems?.[index]) {
            this.stagedItems[index].quantity = quantity.value;
            if (quantityError && positiveInteger(quantity.value))
              this.stagedItems[index].error = "";
            this.input.value = csvItems(this.stagedItems);
          }
          if (!row.matches?.length && quantityError)
            this.setStatus(
              "Quantity updated. Review products again to match this row against the live catalog.",
            );
          this.updateActions();
        });
        quantityWrap.append(quantityLabel, quantity);
        article.append(main, quantityWrap);
        this.rowsElement.append(article);
      });
      this.updateActions();
    }

    updateActions() {
      const { items, errors } = aggregateRows(this.rows);
      const ready = this.rows.length > 0 && errors.length === 0;
      if (this.addButton)
        this.addButton.disabled =
          !ready || !!this.adding || this.completed || this.uncertain;
      this.saveButton.disabled = !ready || !!this.adding;
      this.reviewButton.disabled = !!this.adding || this.uncertain;
      this.input.disabled = !!this.adding || this.uncertain;
      this.querySelector("[data-qo-summary]").textContent = ready
        ? `${items.length} unique product options · ${items.reduce((sum, item) => sum + item.quantity, 0)} units to add`
        : "Resolve every marked row before adding your list.";
    }

    async readCart() {
      const response = await root.fetch(this.dataset.cartUrl + ".js", {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!response.ok)
        throw new Error(
          "Your cart could not be checked. Open your cart and try again.",
        );
      const cart = await response.json();
      cartQuantities(cart);
      return cart;
    }

    updateNativeCart(response, cart) {
      const minicart = root.document.querySelector("mini-cart");
      try {
        if (response?.sections && minicart?.renderContents)
          minicart.renderContents(response);
        else minicart?.updateSectionContents?.();
      } catch (_) {
        /* The cart link still opens the authoritative full cart. */
      }
      root.document.dispatchEvent(
        new CustomEvent("cart:item-add", {
          detail: { items: response?.items || [], context: "QuickOrder", cart },
        }),
      );
      const cartEvents =
        typeof PUB_SUB_EVENTS !== "undefined"
          ? PUB_SUB_EVENTS
          : root.PUB_SUB_EVENTS;
      if (root.publish && cartEvents?.cartItemAdd)
        root.publish(cartEvents.cartItemAdd, {
          source: "QuickOrder",
          items: response?.items || [],
        });
      root.document.dispatchEvent(
        new CustomEvent("saber:cart-updated", { detail: { cart } }),
      );
    }

    async addItems() {
      if (
        this.adding ||
        this.completed ||
        this.uncertain ||
        this.dataset.catalogOnly === "true"
      )
        return;
      const { items, errors } = aggregateRows(this.rows);
      if (errors.length || !items.length) return;
      this.adding = true;
      this.setAttribute("aria-busy", "true");
      this.render();
      this.setStatus("Checking your cart and adding your reviewed products…");
      let before,
        result,
        attempted = false;
      try {
        before = await this.readCart();
        const quantityErrors = validateAggregate(items, before);
        if (quantityErrors.length) throw new Error(quantityErrors.join(" "));
        const minicart = root.document.querySelector("mini-cart");
        const body = {
          items: items.map((item) => ({
            id: item.id,
            quantity: item.quantity,
          })),
        };
        if (minicart?.getSectionsToRender) {
          body.sections = [
            ...new Set(
              minicart
                .getSectionsToRender()
                .map((section) => section.section)
                .filter(Boolean),
            ),
          ].slice(0, 5);
          body.sections_url = root.location.pathname;
        }
        attempted = true;
        const response = await root.fetch(this.dataset.cartAddUrl + ".js", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });
        result = await response.json();
        if (!response.ok || result.status)
          throw new Error(
            typeof result.description === "string"
              ? result.description
              : "Some products could not be added. Your cart will be checked before you try again.",
          );
        const after = await this.readCart();
        const progress = reconcile(items, before, after);
        if (progress.some((item) => item.remaining > 0))
          throw new Error(
            "Not all requested quantities were added. Your cart has been checked.",
          );
        this.completed = true;
        this.input.value = "";
        this.updateNativeCart(result, after);
        this.setStatus(
          `Your reviewed products are in your cart. ${progress.reduce((sum, item) => sum + item.added, 0)} units added. Review your cart to continue to checkout.`,
        );
      } catch (error) {
        if (!attempted) this.setStatus(error.message, true);
        else {
          try {
            const after = await this.readCart();
            const progress = reconcile(items, before, after);
            const remaining = progress
              .filter((item) => item.remaining > 0)
              .map((item) => ({
                sku: item.sku,
                quantity: item.remaining,
                variantId: item.id,
                title: item.productTitle,
                url: item.url,
              }));
            const added = progress.reduce((sum, item) => sum + item.added, 0);
            this.updateNativeCart(result, after);
            if (!remaining.length) {
              this.completed = true;
              this.input.value = "";
              this.setStatus(
                "Your cart contains the requested quantities. Review your cart before placing your order.",
              );
            } else {
              this.rows = [];
              this.input.value = csvItems(remaining);
              this.stagedItems = remaining;
              this.setStatus(
                `${error.message} ${added ? added + " units are now in your cart. Your list contains only the remaining items." : "Your cart quantities did not increase."} Review the remaining list before trying again.`,
                true,
              );
              // Preserve exact product references, including products without SKUs, for a deliberate fresh review.
            }
          } catch (_) {
            this.uncertain = true;
            this.setStatus(
              "The order request could not be confirmed. To prevent duplicate items, further ordering is paused. Open your cart to check quantities, then refresh this page before starting another list.",
              true,
            );
          }
        }
      } finally {
        this.adding = false;
        this.removeAttribute("aria-busy");
        this.render();
      }
    }
  }

  if (root.customElements && !root.customElements.get("saber-quick-order"))
    root.customElements.define("saber-quick-order", QuickOrder);
  return {
    parseRows,
    normalizeSku,
    resolveRows,
    matchProducts,
    aggregateRows,
    validateAggregate,
    cartQuantities,
    reconcile,
    QuickOrder,
  };
});
